import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CopilotAmbient from "./CopilotAmbient";
import CopilotEmptyState from "./CopilotEmptyState";
import CopilotComposer from "./CopilotComposer";
import CopilotContextBar from "./CopilotContextBar";
import CopilotSlashMenu, { parseSlash, PROJECT_COMMAND } from "./CopilotSlashMenu";
import CopilotHeader from "./CopilotHeader";
import CopilotHistory from "./CopilotHistory";
import CopilotProjectOverview from "./CopilotProjectOverview";
import CopilotProjectPanel from "./CopilotProjectPanel";
import CopilotProjectSummary from "./CopilotProjectSummary";
import CopilotRequirementsPanel from "./CopilotRequirementsPanel";
import ProposalDocumentPanel from "./ProposalDocumentPanel";
import CopilotSourcePanel from "./CopilotSourcePanel";
import CopilotTenderPanel from "./CopilotTenderPanel";
import { sectionIsWritten } from "../../../services/sectionDraft";
import CopilotTurn from "./CopilotTurn";
import { CopilotReasoningLive } from "./CopilotReasoning";
import useWorkspaceCopilot, {
  QUALIFY_MARKER,
  PROPOSAL_FLOW_MARKER,
  NARRATION_MARKER,
  PROPOSAL_STEP_MARKER,
  PROPOSAL_DONE_MARKER,
  projectFromRows,
} from "./useWorkspaceCopilot";
import api from "../../chat/api";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchDiscover } from "../../../services/tendersApi";
import { waitForUploadStatus } from "../../../services/uploadStatus";
import { markOnboarding } from "../../../onboarding/onboardingStore";

/* RFP Copilot — the workspace-wide assistant.
 *
 * Built fresh under RFP Manager rather than adapted from /ai-copilot: that page
 * carries Contracts / Invoices / Spreadsheets wiring and ~1000 lines of legacy
 * CSS, none of which belongs in an RFP-native tool. Everything here is Tailwind,
 * matching the rest of this module.
 *
 * Connected to ws/workspace-copilot/<chat_id>/. Socket mechanics live in
 * useWorkspaceCopilot so this file stays a view.
 */


/* Attached context is a property of ONE conversation. Chat A keeps project X,
 * chat B keeps project Y, and a brand-new chat starts with nothing until the
 * user pins something. There is deliberately no "inherit the last project"
 * fallback: it guessed right for people working one bid at a time and silently
 * guessed wrong for everyone else, and a wrong pinned project is the most
 * expensive mistake this page can make. Returning to the page keeps the chip
 * because the hook resumes the same chat id, not because context leaks
 * between conversations.
 */
const CTX_KEY = (chatId) => `rfp_copilot_ctx_${chatId}`;

/* Which conversation "Chat with project" opened for each project, so the next
 * click goes back to it instead of starting another. Per browser, like the rest
 * of this page's remembered state; if the chat is gone (deleted, or another
 * user's), the button simply starts a new one. */
const PROJECT_CHATS_KEY = "rfp_copilot_project_chats";
const readProjectChat = (projectId) => {
  try {
    return JSON.parse(localStorage.getItem(PROJECT_CHATS_KEY) || "{}")[projectId] || null;
  } catch {
    return null;
  }
};
const rememberProjectChat = (projectId, chatId) => {
  try {
    const all = JSON.parse(localStorage.getItem(PROJECT_CHATS_KEY) || "{}");
    all[projectId] = chatId;
    localStorage.setItem(PROJECT_CHATS_KEY, JSON.stringify(all));
  } catch {
    /* storage unavailable: the next click starts a new chat */
  }
};

const readContext = (chatId) => {
  try {
    const raw = localStorage.getItem(CTX_KEY(chatId));
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];   // corrupt storage must never take the page down
  }
};

const writeContext = (chatId, items) => {
  try {
    localStorage.setItem(CTX_KEY(chatId), JSON.stringify(items));
  } catch {
    /* quota or private mode — the chip still works for this session */
  }
};

/* The sidebar reads the user's name from localStorage at login; this page falls
 * back to the same keys rather than requiring a prop the route never passed.
 * One source of truth, so the rail's initials and the greeting can't disagree. */
const storedDisplayName = () => {
  try {
    return (
      localStorage.getItem("full_name") ||
      localStorage.getItem("fullName") ||
      localStorage.getItem("username") ||
      ""
    );
  } catch {
    return "";
  }
};

const RfpCopilotPage = ({ displayName = storedDisplayName() }) => {
  // Declared before the hook that consumes it and assigned further down, once
  // the real handler exists. A ref rather than reordering the file: the handler
  // touches state this same hook feeds, so it cannot be moved above it.
  const openProjectPanelRef = useRef(null);

  const {
    messages,
    status,
    streaming,
    activeTool,
    trace,
    stopping,
    focus,
    lastClose,
    lastRewrite,
    clearLastRewrite,
    lastExport,
    clearLastExport,
    chatId,
    send,
    stop,
    respondToApproval,
    reset,
    loadSession,
    postLocal,
    patchLocal,
  } = useWorkspaceCopilot({
    // Server-restored transcripts carry the project each turn was pinned to;
    // queue it so the chip survives logout, new browsers, other devices.
    onRestoreRows: (cid, rows) => queueRestoredProject(cid, rows),
    // "Add the RFP" opens the project panel — that is where the drop zone is.
    onOpenProject: (p) => openProjectPanelRef.current?.(p),
  });
  const [draft, setDraft] = useState("");
  // What the next message is attached to. One project today; files and
  // sections drop into the same array later without changing the bar.
  // Context state is tagged with the chat it belongs to. Persist and seed are
  // two effects racing over the same storage on a chat switch — untagged, the
  // persist effect fired first with the NEW id and the OLD items, copying chat
  // A's project into chat B's key, and the seed effect then read back the
  // contamination it had just written. The tag lets the persist effect refuse
  // to write items that are not this chat's, which makes cross-chat leakage
  // structurally impossible rather than merely unlikely.
  const [ctxState, setCtxState] = useState(() => ({
    owner: chatId,
    items: readContext(chatId),
  }));
  // During the one render where the owner hasn't caught up with a chat switch,
  // read the new chat's real context directly — the user must never see the
  // previous chat's chip, even for a frame.
  const context = ctxState.owner === chatId ? ctxState.items : readContext(chatId);
  const setContextItems = useCallback(
    (updater) =>
      setCtxState((prev) => {
        const base = prev.owner === chatId ? prev.items : readContext(chatId);
        return {
          owner: chatId,
          items: typeof updater === "function" ? updater(base) : updater,
        };
      }),
    [chatId]
  );
  const [slashDismissed, setSlashDismissed] = useState(false);
  // Bumped after a turn finishes so reopening the drawer shows the chat that was
  // just had, instead of a list that stops one conversation short.
  const [historyKey, setHistoryKey] = useState(0);
  const composerRef = useRef(null);
  const tailRef = useRef(null);
  const composerBoxRef = useRef(null);
  const scrollRef = useRef(null);
  // Whether the reader is parked at the bottom. Held in a ref as well as state
  // because the streaming effect reads it on every token and must not re-run
  // just because it changed.
  const followRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  /* How tall the composer currently is. Measured rather than assumed, because
   * it changes with the context chip, the slash menu and the textarea growing —
   * and this number is the transcript's bottom padding, so a wrong guess either
   * hides the last line or leaves a gap. */
  const [composerH, setComposerH] = useState(96);
  const menuKeyRef = useRef(null);

  const isEmpty = messages.length === 0;
  const navigate = useNavigate();
  const location = useLocation();

  // Discover's best three for the empty screen: stored matches, no model call.
  // Not when a project is attached: that screen is the project's overview, and
  // other tenders are a distraction from the one being worked on.
  const hasProject = context.some((c) => c.kind === "project");
  const [picks, setPicks] = useState(null);
  useEffect(() => {
    if (!isEmpty || hasProject) return undefined;
    let alive = true;
    fetchDiscover({ tab: "for_you", sort: "best", limit: 3 })
      .then((data) => alive && setPicks(data))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [isEmpty, hasProject]);

  /* Where each reply's copy/rate controls belong.
   *
   * A reply is often followed by flow rows — step lines, a card — that are part
   * of the same utterance. The icons belong at the END of that run, not wedged
   * between the sentence and the rows it introduces.
   *
   * Computed here rather than in the row, because a row cannot see past its
   * immediate neighbour: with three step lines in a row, the last one has no
   * way to find the assistant message three rows back that it is rating.
   *
   * Maps row index -> the assistant message whose icons render there.
   */
  /* Where the copy and rating controls go: ONE set per utterance, at its end.
   *
   * Grouped as `speakerAt` groups the name, so an utterance opens with "Sensa"
   * at the top and closes with its controls at the bottom, whatever it contains
   * in between.
   *
   * ONE deliberate difference: `speakerAt` lets a `trace` row open a turn (it
   * has to, or a restored run draws its "N steps" chip above the name), while
   * here `trace` stays voiceless. A collapsed account of work is not something
   * to rate or copy, so it must never be the row the controls land on. Do not
   * "fix" the drift by aligning the two sets — that reintroduces the bug.
   *
   * The earlier version anchored per assistant MESSAGE and walked forward over
   * anything that looked like it belonged. That worked live and broke on
   * reload: narration saved before the SAY:: marker existed comes back
   * indistinguishable from a reply, so each line started a run of its own and
   * the transcript grew a set of icons after every step. Deriving the anchor
   * from the RUN instead fixes those rows too — there is one run, so there is
   * one set of controls, no matter how its rows are labelled.
   *
   * What gets rated is the last real reply in the run; narration is skipped
   * where it can be identified, since there is nothing in "Starting on the
   * first section now." to rate.
   */
  const feedbackAt = useMemo(() => {
    const SENSA = new Set([
      "assistant", "qualify", "recall", "approval", "requirements", "tenders",
      "proposalCard", "proposalStep", "proposalSetup",
    ]);
    const VOICELESS = new Set(["proposalFlow", "trace"]);
    const map = {};

    let lastIdx = -1;   // last visible row of the run in progress
    let rateable = null; // the message whose text the controls act on

    const close = () => {
      if (lastIdx >= 0 && rateable) map[lastIdx] = rateable;
      lastIdx = -1;
      rateable = null;
    };

    for (let i = 0; i < messages.length; i += 1) {
      const m = messages[i];
      if (VOICELESS.has(m.role)) continue;
      if (m.role === "assistant" && !m.text?.trim()) continue;
      if (!SENSA.has(m.role)) {
        close();
        continue;
      }
      lastIdx = i;
      if (m.role === "assistant" && m.text?.trim() && !m.typed && !m.narration) {
        rateable = m;
      }
    }
    close();
    return map;
  }, [messages]);

  /* Which rows introduce the speaker.
   *
   * Sensa answers in more than one shape: prose, a card, a one-line record, a
   * form. Consecutive rows of any of those are ONE utterance and get ONE name.
   * Two "Sensa" headers inside a single answer says two things spoke when one
   * did, and splits a continuous thought into what look like separate replies.
   *
   * Every branch used to decide this for itself, so a reply after a card - or
   * after another reply - wore the name twice. One rule instead, computed here
   * because it is a question about the SEQUENCE: a row that can only see `prev`
   * cannot answer it, since the row above it may draw nothing at all.
   */
  const speakerAt = useMemo(() => {
    const SENSA = new Set([
      "assistant", "qualify", "recall", "approval", "requirements", "tenders",
      "proposalCard", "proposalStep", "proposalSetup",
      /* `trace` belongs here even though it is an account of work rather than
       * something said. Live, the collapsed summary renders INSIDE the assistant
       * row, under the name. Restored, it is a row of its own — and if it cannot
       * open a turn it renders above whichever row can, putting "N steps" on
       * screen before "Sensa". It opens a turn only when it comes first; mid-turn
       * `speaking` is already true and it draws no second header. */
      "trace",
    ]);
    /* No voice of its own: a flow bookmark draws nothing, so it neither opens an
     * utterance nor ends one and is stepped over. */
    const VOICELESS = new Set(["proposalFlow"]);
    const map = {};
    let speaking = false;
    for (let i = 0; i < messages.length; i += 1) {
      const m = messages[i];
      if (VOICELESS.has(m.role)) continue;
      // An assistant row with nothing in it yet renders no prose, so it cannot
      // carry the name - otherwise a card-opening turn shows "Sensa" above a gap.
      if (m.role === "assistant" && !m.text?.trim()) continue;
      // Same rule for a trace: CopilotTurn returns null when it has no steps, so
      // marking it as the opener would lose the header for the whole turn.
      if (m.role === "trace" && !m.steps?.length) continue;
      if (!SENSA.has(m.role)) { speaking = false; continue; }
      if (!speaking) map[i] = true;
      speaking = true;
    }
    return map;
  }, [messages]);

  /* The question this turn is answering. Anchoring to the ANSWER scrolled the
   * question off the top the instant the first token landed, so a turn opened
   * with a reply to something you could no longer see. */
  const lastUserIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) if (messages[i].role === "user") return i;
    return -1;
  }, [messages]);

  // The panel belongs to the WAIT, not the whole turn. Once the answer is
  // streaming, the answer is the progress indicator — leaving a "Working…" box
  // sitting under text that is visibly arriving reads as a stuck spinner. It
  // comes back if the agent starts another tool after speaking.
  /* The project panel. Kept on the page rather than per-chat: whether you like
     working with the files visible is a habit, not a property of a thread. */
  const [panelProject, setPanelProject] = useState(() => {
    try {
      const raw = localStorage.getItem("rfp_copilot_panel");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (panelProject) localStorage.setItem("rfp_copilot_panel", JSON.stringify(panelProject));
      else localStorage.removeItem("rfp_copilot_panel");
    } catch {
      /* storage unavailable — the panel just won't be remembered */
    }
  }, [panelProject]);

  /* Two states, because opening and closing are not the same shape.
   *
   * `panelProject` is what the panel shows; `panelOpen` is whether it is on
   * screen. They are separate so the panel can still be MOUNTED while it slides
   * out — unmounting on click would delete the thing that is meant to be
   * animating, which is why a closing panel used to just blink away. */
  const [panelOpen, setPanelOpen] = useState(false);
  /* A scoring area drilled into from the qualify card. It occupies the same
   * column as the project panel and takes precedence while set — one right-hand
   * surface, two things it can show, never both. */
  const [panelAspect, setPanelAspect] = useState(null);
  /* The passages behind one answer, opened by clicking a citation in it.
   * `{ sources, activeN }` — the whole list, and which mark was clicked. */
  const [panelSource, setPanelSource] = useState(null);
  /* A tender opened from a Tender Search card in the chat.
   * `{ tenderId, initial }` — `initial` is the clicked row, so the header can
   * render before the full tender arrives. */
  const [panelTender, setPanelTender] = useState(null);
  useEffect(() => {
    if (!panelProject && !panelAspect && !panelSource && !panelTender) return undefined;
    // One frame at the collapsed width first, so the browser has something to
    // transition FROM. Setting both in the same commit animates nothing.
    const raf = requestAnimationFrame(() => setPanelOpen(true));
    return () => cancelAnimationFrame(raf);
  }, [panelProject, panelAspect, panelSource, panelTender]);

  /* Whether the user has chosen to hide the rail. Kept apart from the panel:
   * opening a project hides the rail too, but that is a consequence, not a
   * preference — closing the project should give back the rail they had, not
   * the one the project imposed. One flag for the choice, one for the
   * circumstance, and the layout reads both. */
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try {
      return localStorage.getItem("rfp_copilot_rail_collapsed") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("rfp_copilot_rail_collapsed", railCollapsed ? "1" : "0");
    } catch {
      /* storage unavailable — the rail just won't be remembered */
    }
  }, [railCollapsed]);

  /* The unmount timer has to be cancellable.
   *
   * closePanel schedules a wipe 300ms out. Anything that OPENS a panel inside
   * that window — clicking away from the panel straight onto a link that opens
   * another one, which is now a single gesture — would be erased by a timer
   * fired for the panel before it. Every opener clears it first. */
  /* Where the proposal flow has got to.
   *
   * One value drives the whole flow, exactly as the handoff specifies:
   * setup → planning → outline → mode → writing → sectionDone → complete.
   * Phase 1 only ever sets "planning"; the cards that render the later steps
   * arrive with their own phases and read this same value.
   *
   * Kept on the page rather than inside the setup card because the steps after
   * setup own the document panel and the rail, which are the page's to open. */
  const [proposal, setProposal] = useState(null);

  /* The flow, as a thread.
   *
   * Each step is posted as its own row and stays where it happened. When the
   * user acts on it, that row COLLAPSES to one line and the next step is posted
   * beneath it — so the conversation reads as a sequence of things that were
   * decided, not as one card that keeps changing its mind.
   *
   * `flowRowRef` holds the row currently showing a card. Only one is ever live:
   * posting the next step always resolves the previous, so two open cards
   * cannot both be answerable. */
  const flowRowRef = useRef(null);

  /* Write a flow row to the server as well as to the screen.
   *
   * postLocal alone is memory only, so switching chats used to wipe the whole
   * thread — every step, and the download card with it. Fire and forget: a
   * failed write must never interrupt the proposal, and the worst case is the
   * history being thinner than the session that produced it. */
  const persistFlowRow = useCallback(
    (marker, payload) => {
      if (!chatId) return;
      api
        .post("/api/v1/rfp/copilot/log-turn/", {
          chat_id: chatId,
          bot_only: true,
          user_message: "-",
          bot_message: marker + JSON.stringify(payload),
        })
        .catch(() => {});
    },
    [chatId]
  );

  /* The flow's current values, readable from a callback without re-creating it.
   *
   * These handlers used to read `prev` inside a setProposal updater, which is
   * where the numbers were guaranteed fresh. They no longer return new state -
   * they post and patch rows - so they need the current values without a stale
   * closure and without `proposal` in every dependency array. */
  const proposalRef = useRef(null);
  proposalRef.current = proposal;

  /* Sensa saying something in its own voice, mid-flow.
   *
   * The proposal runs on the CLIENT - a sequence of API calls the page drives,
   * with no model turn between the steps - so nothing writes the sentences that
   * would normally introduce a card. The run appeared as cards materialising
   * one after another with no one speaking, which reads as a wizard advancing
   * rather than an assistant working.
   *
   * Authored here rather than asked of the model: these are about what the
   * INTERFACE is doing next, they must appear instantly, and paying for a model
   * round trip to say "here's the outline" would add seconds to every step.
   *
   * Persisted as ordinary assistant text so a reopened conversation reads as
   * the conversation it was. Restored rows carry no `typed` flag, which stops a
   * month-old thread typing itself out again on open. */
  /* A line Sensa says on its own behalf.
   *
   * `busy` keeps the streaming caret alive after the line finishes typing,
   * for the case where narration is followed by a wait with nothing on screen
   * — the clarification check runs for twenty-odd seconds behind a single
   * sentence, and a finished sentence sitting alone is indistinguishable from
   * a hang. It clears itself: the caret shows only while the line is the LAST
   * thing in the transcript, so posting anything after it ends the effect with
   * no bookkeeping. It is local-only too, so a reload never restores a caret
   * with no work behind it.
   */
  const say = useCallback(
    (text, { busy = false } = {}) => {
      if (!text) return;
      postLocal({ role: "assistant", text, typed: true, busy });
      if (!chatId) return;
      api
        .post("/api/v1/rfp/copilot/log-turn/", {
          chat_id: chatId,
          bot_only: true,
          user_message: "-",
          bot_message: NARRATION_MARKER + text,
        })
        .catch(() => {});
    },
    [postLocal, chatId]
  );

  const openFlowCard = useCallback(
    (kind, extra = {}) =>
      setProposal((prev) => {
        const next = { ...(prev || {}), ...extra, kind, step: kind };
        // Posted from inside the updater so the row carries state that is
        // current, not a closure captured a render ago.
        flowRowRef.current = postLocal({ role: "proposalCard", ...next });

        /* Only the terminal card is written to the server. The interactive
         * ones are deliberately not: an outline approval restored weeks later
         * as a live card would be answerable a second time, against a proposal
         * that has moved on. Downloads have no such problem — they just export
         * whatever the project holds now. */
        // A mid-run export is an ACTION, not a milestone. Persisting it wrote
        // "Proposal complete" into the permanent thread of a proposal that was
        // only half written, and that stale card then sat above the live
        // section card on every return to the chat.
        if (kind === "complete" && !next.partial) {
          persistFlowRow(PROPOSAL_DONE_MARKER, {
            projectId: next.projectId,
            projectName: next.projectName || "",
            templateId: next.templateId || null,
            // WHICH PATH WROTE IT decides which formats the card offers, so it
            // has to survive the reload. Left out, the restored card fell back
            // to offering both and put a PDF button on a one-go proposal — and
            // that PDF renderer walks a section tree a one-go draft does not
            // have.
            oneGo: !!next.oneGo,
          });
        }
        return next;
      }),
    [postLocal, persistFlowRow]
  );

  /* Collapse the live card into its one-line record. */
  const closeFlowCard = useCallback(
    (label, detail = "", tone = "done", extra) => {
      if (!flowRowRef.current) return;
      /* `steps` is the account of HOW the step was done — the phases the server
       * reported while it ran. Kept on the record rather than discarded with the
       * card, so "which documents did it actually read" is answerable after the
       * fact instead of only while watching. Undefined for the steps that have
       * no phases of their own, and the row simply renders one line then. */
      const summary = { label, detail, tone, ...(extra || {}) };
      patchLocal(flowRowRef.current, { resolved: true, summary });
      flowRowRef.current = null;
      // A resolved step is just a line of history, so it persists as one — the
      // restored transcript rebuilds it as a ProposalStepRow, not as a card.
      persistFlowRow(PROPOSAL_STEP_MARKER, summary);
    },
    [patchLocal, persistFlowRow]
  );

  /* Work the user must not talk over.
   *
   * `streaming` alone left the composer live through the multi-minute planning
   * step, so a second question could arrive mid-flow and race the work it was
   * about. Anything that owns the conversation for a stretch belongs here.
   *
   * Declared HERE, below `proposal`, not up with isEmpty and the other derived
   * flags where it looks like it belongs: `proposal` is a const and const does
   * not hoist, so reading it earlier is a temporal dead zone crash on mount —
   * and the build does not catch it, because it is a runtime error. */
  /* Busy for the WHOLE flow, not just the parts where a server is working.
   *
   * The card is the current response until the proposal is finished or the user
   * stops — including the steps that are waiting on THEM, like approving the
   * outline. Freeing the composer there would let a new question arrive while a
   * card is still asking one, and the answer to it would render after a card
   * that is no longer the thing in front of the user.
   *
   * Every step here is escapable: the square clears the flow. */
  const flowBusy =
    proposal?.chatId === chatId &&
    !!proposal?.step &&
    /* Only the steps where a SERVER is working hold the composer.
     *
     * The steps that wait on the user - approving an outline, picking a mode,
     * deciding about a finished section - used to be held too, which made the
     * flow a wizard: the one moment someone has an opinion about the draft was
     * the one moment they could not say it. Now those steps show an arrow, and
     * a typed instruction reaches Sensa, which can call rewrite_section.
     *
     * `writing` stays held. A second request mid-draft would race the loop that
     * is already writing the section it refers to. */
    !["complete", "stopped", "outline", "mode", "sectionDone"].includes(
      proposal.step
    );
  const busy = streaming || flowBusy;

  /* One button, so it must cancel whatever is actually running. A gate without
   * a working exit is a lock with no key: anything added to `busy` above has to
   * be stoppable here too. */
  const stopEverything = useCallback(() => {
    if (streaming) stop();
    // Not null: the resume effect would find the flow marker and reopen it
    // on the very next render, which is why stop appeared to do nothing.
    if (flowBusy) {
      // Collapse the live card into a record of the stop, so the thread shows
      // where it was abandoned rather than simply losing the card.
      closeFlowCard("Stopped", "ask me to write it again whenever you're ready", "redo");
      setProposal((prev) => (prev ? { ...prev, step: "stopped" } : prev));
    }
  }, [streaming, stop, flowBusy, closeFlowCard]);

  /* The setup card's unsaved values, so reopening it to change one thing does
   * not reset the rest. A ref, not state: this is read once when the next card
   * mounts, and storing it in state would re-render the whole transcript on
   * every keystroke in the card. */
  const proposalCarryRef = useRef(null);
  const rememberProposalSetup = useCallback((v) => {
    proposalCarryRef.current = v;
  }, []);

  /* Leave a line in the transcript for each step that completes.
   *
   * The flow's cards replace one another, so without this the conversation ends
   * up holding no evidence that twenty minutes of work happened. These rows are
   * the history — and they also fix the ordering, because a message typed
   * mid-flow now lands after them rather than above a card pinned to the
   * bottom. */


  const startProposal = useCallback(
    ({ projectId, projectName }) => {
      // Stamped with the conversation it belongs to. Without this the flow is
      // page state with no owner: switching chats carried a live outline card
      // into a different conversation, where it described a project that chat
      // had never mentioned — and pressing Continue there would have written a
      // proposal from it.
      setProposal({ projectId, projectName, chatId });
      say("Before I start — how would you like me to work?");
      openFlowCard("approach", { projectId, projectName, chatId });

      /* Recorded so a reload or a chat switch can find its way back. Fire and
       * forget: failing to write the bookmark must not stop the proposal the
       * user just asked for. */
      if (chatId) {
        api
          .post("/api/v1/rfp/copilot/log-turn/", {
            chat_id: chatId,
            user_message: "Generate proposal",
            bot_message:
              PROPOSAL_FLOW_MARKER +
              JSON.stringify({ project_id: projectId, project_name: projectName || "" }),
          })
          .catch(() => {});
      }
    },
    [chatId, openFlowCard, say]
  );

  /* One place decides which path opens, so the checkpoint can hand off to
   * either without duplicating the branch. */
  const startWriting = useCallback(
    ({ approach, projectId, projectName }) => {
      if (approach === "oneGo") {
        say("Reading the whole RFP and writing the draft now.");
        openFlowCard("oneGo", { projectId, projectName });
        return;
      }
      say("Let me read the RFP and work out a structure.");
      openFlowCard("plan", { projectId, projectName });
    },
    [openFlowCard, say]
  );

  /* The fork: full pipeline, or one pass over the whole RFP.
   *
   * Either way the CLARIFICATION CHECKPOINT runs first. It asks about
   * contradictions between our own documents and about things the RFP wants
   * that the KB does not have — before a word is written, because a one-go
   * proposal is a single LLM call and there is no mid-flight point to stop at.
   *
   * A checkpoint with nothing to ask must be invisible: when the list comes
   * back empty the flow goes straight on, and the user never learns it ran.
   * The request failing is treated the same way — writing blind is what we did
   * before this existed, so it is a safe degradation, not an error state. */
  const approachChosen = useCallback(
    async ({ approach, projectId, projectName }) => {
      const oneGo = approach === "oneGo";
      closeFlowCard(
        oneGo ? "Writing in one pass" : "Writing section by section",
        "",
        "step"
      );

      let questions = [];
      /* The checkpoint takes about a minute — it plans the outline, searches the
       * knowledge base and reconciles what it finds. That used to be one frozen
       * narration line, which is indistinguishable from a hung request. Now each
       * phase lands as its own step row, the same one-line record the rest of the
       * flow leaves behind.
       *
       * THE KEY IS MINTED HERE rather than returned by the server: the call is a
       * single blocking POST, so by the time a response could carry a key the
       * work is already finished. Subscribing first and passing the key in is
       * what makes the frames arrive while the request is still open. */
      const statusKey = `clarify_${projectId}_${Math.random().toString(36).slice(2, 10)}`;

      /* ONE ROW IN MOTION AT ALL TIMES.
       *
       * Posting only finished rows left the column static through the parts that
       * actually take the time - a minute of retrieval under three ticked rows
       * is indistinguishable from a dead request. So a phase posts its row when
       * it STARTS, spinning, and that same row is patched in place when it ends.
       * The eye always has something moving, and no row ever claims work that
       * has not happened. */
      const runningRef = { current: null };
      const begin = (label) => {
        runningRef.current = postLocal({ role: "proposalStep", label, tone: "running" });
      };
      const settle = (label, detail = "") => {
        if (runningRef.current) patchLocal(runningRef.current, { label, detail, tone: "step" });
        else postLocal({ role: "proposalStep", label, detail, tone: "step" });
        runningRef.current = null;
      };
      const count = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

      const watch = waitForUploadStatus({
        statusKey,
        timeoutMs: 5 * 60 * 1000,
        onProgress: (d) => {
          const m = d?.metadata || {};
          // Counts come off the frame's metadata, not its message. Reusing the
          // message put "Searched your knowledge base" in the label and
          // "Searched your knowledge base - 10 sources" beside it, saying the
          // same thing twice on one line.
          if (m.phase === "read") begin("Reading the solicitation");
          else if (m.phase === "outline")
            settle("Planned the response", count(m.sections || 0, "section"));
          else if (m.phase === "retrieve") begin("Searching your knowledge base");
          else if (m.phase === "retrieved") {
            settle("Searched your knowledge base", count(m.sources || 0, "source"));
            // The reconcile call is another 15-25s with nothing else to show.
            begin("Checking for conflicts and gaps");
          } else if (m.phase === "done") settle("Checked your documents");
        },
      });
      try {
        const res = await api.post(
          `/api/v1/rfp/folders/${projectId}/proposal/clarify/`,
          { status_key: statusKey }
        );
        questions = res.data?.questions || [];
      } catch {
        questions = [];
      } finally {
        watch.cancel();
        // A spinner left turning after the request returned is the exact thing
        // these rows exist to prevent, so nothing may outlive the call - including
        // when the socket never delivered the closing frame.
        if (runningRef.current) settle("Checked your documents");
      }

      if (questions.length) {
        say(
          questions.length === 1
            ? "One thing I need you to settle first."
            : `${questions.length} things I need you to settle first.`
        );
        openFlowCard("clarify", { projectId, projectName, questions, approach });
        return;
      }
      startWriting({ approach, projectId, projectName });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeFlowCard, openFlowCard, say]
  );

  /* Answers are saved by the card itself, onto the folder — so they still
   * apply when a section is regenerated next week. Nothing to pass along. */
  const clarifyDone = useCallback(
    ({ projectId, projectName, approach, questions }) => {
      closeFlowCard(
        `Confirmed ${questions?.length || 0} detail${(questions?.length || 0) === 1 ? "" : "s"}`,
        "",
        "step"
      );
      startWriting({ approach, projectId, projectName });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeFlowCard]
  );

  /* The one-go draft landed — one card, one document, straight to the panel. */
  const oneGoDone = useCallback(
    ({ words }) => {
      closeFlowCard("Proposal written", words ? `${words} words` : "", "step");
      say("Here's the draft. Anything marked NEEDS INPUT is where I had no evidence.");
      // Flags the completion card so it offers DOCX only, plain, for this path.
      openFlowCard("complete", { oneGo: true });
    },
    [closeFlowCard, openFlowCard, say]
  );

  const oneGoFailed = useCallback(() => {
    closeFlowCard("Couldn't write the proposal", "ask me to try again", "redo");
  }, [closeFlowCard]);

  /* The outline exists — collapse the planning card and show it for approval. */
  const planReady = useCallback(
    (p) => {
      closeFlowCard("Outline built", "", "step");
      say("Here's the structure I'd propose. Approve it, or tell me what to change.");
      openFlowCard("outline", p || {});
    },
    [closeFlowCard, openFlowCard, say]
  );

  /* A failed plan is a state, not a toast. The row says why and stays put. */
  const planFailed = useCallback(() => {
    closeFlowCard("Couldn't build the outline", "ask me to try again", "redo");
  }, [closeFlowCard]);

  /* "Rebuild outline" posts a fresh planning card rather than reopening the
   * old one — a new attempt is a new event in the thread, and a remounted card
   * is also what lets its started-once guard run the analysis again. */
  const rebuildOutline = useCallback(() => {
    closeFlowCard("Rebuilding the outline", "", "redo");
    openFlowCard("plan", {});
  }, [closeFlowCard, openFlowCard]);


  const outlineConfirmed = useCallback(
    (p) => {
      closeFlowCard(
        "Outline approved",
        `${p?.sections ?? 0} sections · ${p?.subsections ?? 0} subsections`
      );
      // Straight into writing. The mode card used to ask "all at once or
      // section by section", but "all at once" was never built and the
      // question it was reaching for is now asked properly by the approach
      // card, before any outline exists. One remaining option is not a
      // choice — it is a click.
      say("Starting on the first section now.");
      openFlowCard("writing", { ...(p || {}), mode: "section", sectionIdx: 0 });
    },
    [closeFlowCard, openFlowCard, say]
  );

  /* One section is written — stop and show it.
   *
   * `finished` means the writing card was asked for a section index past the
   * end of the outline, which is how the loop learns it is done: the outline is
   * the only thing that knows how many sections there are, and it is the card
   * that reads it. */
  const sectionWritten = useCallback(
    (stats) => {
      if (stats?.finished) {
        // Design first, download second. The look is a property of the FILE,
        // so it is chosen once there is a file to dress — not at setup, where
        // people would be picking a design for something they had not read.
        closeFlowCard("Every section written", "", "done");
        say("That's every section written. Pick a design and I'll produce the file.");
        openFlowCard("template", {});
        return;
      }

      const idx = proposalRef.current?.sectionIdx || 0;
      const words = stats.words ? `${stats.words.toLocaleString()} words` : "";

      /* Auto mode never stops to ask: collapse this section's card and open the
       * next one straight away. Same path the mode card's second option takes -
       * one behaviour reachable from two places, not two implementations. */
      if (proposalRef.current?.mode === "all") {
        /* Ids here too. In auto mode these are the ONLY record of a section —
         * there is no review card to open — so a row that cannot be expanded
         * means twelve sections were written and none can be read back. */
        closeFlowCard(`${idx + 1}. ${stats.sectionTitle || "Section"} written`, words, "done", {
          projectId: proposalRef.current?.projectId,
          sectionId: stats.sectionId,
        });
        openFlowCard("writing", { ...stats, sectionIdx: idx + 1 });
        return;
      }

      /* Section-by-section: collapse the card, say what happened, then ask.
       *
       * This used to MUTATE the same row - the progress card became the review
       * card in place, to avoid leaving a spent progress bar above it.
       * Collapsing solves that, and the mutation cost more than it saved: the
       * card changed shape under the cursor with nothing said about it, and the
       * run kept no record that the section had been written at all. */
      /* Enough to fetch the prose back later, and nothing more.
        *
        * The TEXT is deliberately not stored on the row: a section runs to a
        * thousand words and a chat row is not a document store — it is re-read
        * on every restore. The PHASES are not stored either; they are the live
        * account of work in progress, and once the section exists the section
        * itself is the better answer to "what did it do". */
      closeFlowCard(`${idx + 1}. ${stats.sectionTitle || "Section"} drafted`, words, "done", {
        projectId: proposalRef.current?.projectId,
        sectionId: stats.sectionId,
      });
      /* No line here either. The collapsed row above already says the section
       * was drafted, and the card below carries the counts and the two buttons
       * \u2014 a sentence between them telling the reader to "read it over, then
       * approve it" is a third copy of an instruction already on screen. */
      openFlowCard("sectionDone", { ...stats, sectionIdx: idx });
    },
    [closeFlowCard, openFlowCard, say]
  );

  const sectionFailed = useCallback(
    (error) => {
      closeFlowCard("Couldn't write that section", error || "", "redo");
    },
    [closeFlowCard]
  );

  /* Approve → the next section. The index moves and the step goes back to
   * writing; the card's key includes the index, so it remounts and its
   * started-once guard lets the next section run. */
  const sectionApproved = useCallback(() => {
    const p = proposalRef.current || {};
    const n = (p.sectionIdx || 0) + 1;
    closeFlowCard(
      `${n}. ${p.sectionTitle || "Section"} approved`,
      [
        p.words ? `${p.words.toLocaleString()} words` : "",
        p.subsections ? `${p.subsections} subsections` : "",
      ]
        .filter(Boolean)
        .join(" · "),
      "done",
      { projectId: p.projectId, sectionId: p.sectionId }
    );
    say(`On to ${p.nextTitle || "the next section"}.`);
    openFlowCard("writing", { sectionIdx: n });
  }, [closeFlowCard, openFlowCard, say]);

  /* Asked to export from chat: open the same design chooser the finished run
   * opens, so there is one export path rather than a chat one and a flow one.
   *
   * Works with no proposal state at all — someone can open an old conversation
   * and ask for the file without re-running anything. */
  useEffect(() => {
    /* Wait for the turn to finish before putting the card up.
     *
     * The card frame arrives the instant the tool returns, which is before a
     * single token of the reply has streamed — so opening it immediately put
     * the chooser ABOVE "The download options are on screen", and the sentence
     * pointed backwards at something already passed. Sensa speaks, then shows. */
    if (!lastExport || streaming) return;
    clearLastExport();
    setProposal((prev) => ({
      ...(prev || {}),
      chatId,
      projectId: lastExport.projectId,
      projectName: lastExport.projectName,
    }));
    openFlowCard("template", {
      projectId: lastExport.projectId,
      projectName: lastExport.projectName,
      chatId,
      partial: lastExport.partial,
      sectionsWritten: lastExport.sectionsWritten,
      sectionsTotal: lastExport.sectionsTotal,
    });
  }, [lastExport, streaming, clearLastExport, chatId, openFlowCard]);

  /* A design was picked — collapse the chooser and hand the id to the
   * downloads. Carried on the flow rather than saved anywhere: it is a choice
   * about this export, and pinning it to the project would silently change what
   * the project screen produces too. */
  const templateChosen = useCallback(
    ({ templateId }) => {
      closeFlowCard("Design chosen", "", "step");
      say("Here it is - take it as a PDF or a Word file.");
      openFlowCard("complete", { templateId: templateId || null });
    },
    [closeFlowCard, openFlowCard, say]
  );

  /* Switch to auto mid-flow: approve this one and stop asking from here.
   *
   * By section three most people have calibrated and the gate has become a
   * click they make without reading - and a gate nobody reads is worse than no
   * gate, because it still looks like review. */
  const autoContinue = useCallback(() => {
    const p = proposalRef.current || {};
    const n = (p.sectionIdx || 0) + 1;
    closeFlowCard(
      `${n}. ${p.sectionTitle || "Section"} approved`,
      "writing the rest without stopping",
      "done"
    );
    openFlowCard("writing", { mode: "all", sectionIdx: n });
  }, [closeFlowCard, openFlowCard]);

  /* Rewrite re-runs the SAME index. `attempt` is what forces the remount —
   * without it React keeps the finished card mounted and nothing happens. */
  const sectionRewrite = useCallback(
    ({ instruction }) => {
      const p = proposalRef.current || {};
      // A rewrite is the one thing in this flow that DISCARDS work, so it is
      // the row most worth being able to scroll back to. The instruction is
      // quoted because "why does section 3 read like that" is answered by the
      // sentence that caused it.
      closeFlowCard(
        `Rewriting ${(p.sectionIdx || 0) + 1}. ${p.sectionTitle || "section"}`,
        instruction ? `“${instruction}”` : "no changes specified",
        "redo",
        { projectId: p.projectId, sectionId: p.sectionId }
      );
      openFlowCard("writing", { instruction, sectionIdx: p.sectionIdx || 0 });
    },
    [closeFlowCard, openFlowCard]
  );

  /* Resume where the WORK is, not where the browser last was.
   *
   * The marker says this conversation is writing a proposal. The step comes
   * from the server: no outline yet means it is still planning, an outline with
   * nothing drafted means it is waiting for approval, and anything drafted
   * means writing has begun. Deriving it means the resumed card cannot
   * contradict the project — which a stored step eventually would, the first
   * time someone regenerated a section from the project screen. */
  useEffect(() => {
    const mark = [...messages].reverse().find((m) => m.role === "proposalFlow");
    if (!mark?.projectId) return undefined;
    if (proposal && proposal.chatId === chatId) return undefined;
    // A stopped flow stays stopped until they ask again.
    if (proposal?.step === "stopped") return undefined;

    let alive = true;
    (async () => {
      try {
        const res = await api.get(
          `/api/v1/rfp/folders/${mark.projectId}/proposal/outline/confirm/`
        );
        if (!alive) return;
        const d = res?.data || {};
        const toc = Array.isArray(d.toc) ? d.toc : [];

        /* Which section is actually next.
         *
         * The old version set step "writing" and no sectionIdx, so it defaulted
         * to 0 and redrafted section 1 on every reload - over the top of work
         * that was already saved. The server knows how far this got; the client
         * only has to read it. */
        const firstUnwritten = toc.findIndex((s) => !sectionIsWritten(s));

        let next = { projectId: mark.projectId, projectName: mark.projectName, chatId };
        // Whether resuming should PUT A CARD on screen. A flow with work left
        // should; a finished one has nothing to resume.
        let openCard = true;

        if (!d.sections) {
          next.step = "planning";
        } else if (!d.drafted) {
          next.step = "outline";
        } else if (firstUnwritten === -1) {
          /* Already finished. Record it, show nothing.
           *
           * Opening a chat whose proposal was completed weeks ago used to post
           * a download card unprompted — say "hi" in an old conversation and a
           * proposal you had forgotten about offers you a PDF. Downloads should
           * arrive when the run finishes or when they are asked for, not
           * because a marker exists in the transcript. */
          next.step = "complete";
          openCard = false;
        } else if (firstUnwritten === 0) {
          // Nothing finished yet: pick up the first section where it stopped.
          next = { ...next, step: "writing", sectionIdx: 0 };
        } else {
          /* Whole sections are done. Land on the APPROVAL for the last finished
           * one rather than writing the next.
           *
           * Approval is a client-side gate - the server keeps no record of it -
           * so after a reload we cannot know whether it was given. Resuming into
           * "writing" would silently start the next section on someone who never
           * approved the previous one. Showing the gate again costs one click
           * and can never skip a decision. */
          const done = toc[firstUnwritten - 1];
          const nodes = [done, ...(done.children || [])];
          next = {
            ...next,
            step: "sectionDone",
            sectionIdx: firstUnwritten - 1,
            sectionTitle: done.title,
            subsections: (done.children || []).length,
            words: nodes.reduce((a, n) => a + (n.words || 0), 0),
            isLast: firstUnwritten - 1 >= toc.length - 1,
            nextTitle: toc[firstUnwritten]?.title || "",
            // Without this a resumed card reads "Transition and Exit
            // Management" instead of "10 of 12 · Transition and Exit
            // Management" — the live path passes it, the resume path did not.
            totalSections: toc.length,
          };
        }

        // A resumed flow needs a ROW, not just state — the cards render from
        // the transcript now, so setting the step alone would show nothing.
        const { step, ...rest } = next;
        setProposal(next);
        if (openCard) openFlowCard(step, rest);
      } catch {
        /* Cannot read the project — leave the flow closed rather than guess. */
      }
    })();
    return () => {
      alive = false;
    };
  }, [messages, chatId, proposal, openFlowCard]);

  /* The flow belongs to one conversation.
   *
   * Cards survive a chat switch because they are persisted rows; this is React
   * state, so it must be put away when the conversation changes. It is dropped
   * rather than stashed: the steps after setup are driven by work already on
   * the server (the analysis exists, or it does not), so returning to the chat
   * and asking again resumes from the real state instead of from a snapshot
   * that may have gone stale while you were away. */
  useEffect(() => {
    setProposal((prev) => (prev && prev.chatId !== chatId ? null : prev));
    proposalCarryRef.current = null;
  }, [chatId]);

  const closeTimer = useRef(null);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    // Matches the 300ms transition on both columns. Unmounting sooner cuts the
    // animation off; later leaves an invisible column holding the layout open.
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setPanelProject(null);
      setPanelAspect(null);
      setPanelSource(null);
      setPanelTender(null);
    }, 300);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  /* One right-hand column, two things it can show — so opening either must say
   * what happens to the other.
   *
   * The aspect panel takes precedence in the render. That meant "Open project"
   * while a requirements panel was up set state that could never appear on
   * screen: the click did something, and nothing moved. Opening a project now
   * clears the aspect explicitly rather than relying on a precedence rule to
   * work in a direction it never could. */
  const openProjectPanel = useCallback((p) => {
    clearTimeout(closeTimer.current);
    setPanelAspect(null);
    setPanelSource(null);
    setPanelTender(null);
    setPanelProject(p);
    setPanelOpen(true);
  }, []);

  /* The project's documents, in the viewer, to read beside the conversation.
   *
   * What "Chat with project" opens, and what the project chip opens: the files
   * and the original pages behind answers are one surface, so there is nothing
   * to learn between them. The panel that adds files and tracks their reading
   * is still one click away ("Manage files"). */
  const openProjectDocuments = useCallback(({ projectId, projectName }) => {
    if (!projectId) return;
    clearTimeout(closeTimer.current);
    setPanelAspect(null);
    setPanelTender(null);
    setPanelProject(null);
    setPanelSource({ sources: [], activeN: null, projectId, projectName });
    setPanelOpen(true);
  }, []);

  openProjectPanelRef.current = openProjectPanel;

  const openAspectPanel = useCallback((a) => {
    clearTimeout(closeTimer.current);
    setPanelSource(null);
    setPanelTender(null);
    setPanelAspect(a);
    setPanelOpen(true);
  }, []);

  /* A citation was clicked.
   *
   * Outranks everything else in this column, including the proposal document —
   * every other panel here is opened by a control that is ABOUT the panel, so
   * precedence is a judgement call; this one is a click on a specific mark in a
   * specific sentence, and it has to put that passage on screen or the click
   * visibly did nothing.
   *
   * Re-clicking a different mark while the panel is open just moves the
   * highlight: the list is the same, and closing and reopening it to change
   * which row is marked would throw away the reader's scroll position. */
  /* A tender row in a Tender Search card was clicked.
   *
   * Like a citation, this is a click on one specific thing, so it takes the
   * column over whatever else was open there. Clicking the row that is already
   * open closes it again — the row is the panel's own toggle. */
  const openTenderPanel = useCallback(
    (row) => {
      if (!row?.id) return;
      if (panelOpen && String(panelTender?.tenderId) === String(row.id)) {
        closePanel();
        return;
      }
      clearTimeout(closeTimer.current);
      setPanelSource(null);
      setPanelAspect(null);
      setPanelTender({ tenderId: row.id, initial: row });
      setPanelOpen(true);
    },
    [panelOpen, panelTender, closePanel]
  );

  /* "Start a project from this tender" succeeded in the panel.
   *
   * Pinned, because the chip is what the next message is sent about: the user
   * just chose to work on this bid, and the next question should be about it.
   * Then the column swaps to the new project, where the tender's documents can
   * be watched arriving — the tender panel has done its job. */
  const handleTenderProjectCreated = useCallback(
    ({ projectId, projectName }) => {
      if (!projectId) return;
      setContextItems((prev) => [
        ...prev.filter((c) => c.kind !== "project"),
        { kind: "project", id: projectId, label: projectName || "Project" },
      ]);
      openProjectPanel({ projectId, projectName });
    },
    [setContextItems, openProjectPanel]
  );

  // Read at click time: the callback below is stable, the project is not.
  const contextProjectRef = useRef(null);

  const openSourcePanel = useCallback((n, sources) => {
    if (!sources?.length) return;
    clearTimeout(closeTimer.current);
    // The project the cited passages are from (or, failing that, the one being
    // chatted with), so its other files stay one click away under the page.
    const cited = sources.find((s) => s?.project_id);
    const attached = contextProjectRef.current;
    setPanelSource({
      sources,
      activeN: n,
      projectId: cited?.project_id ?? attached?.id ?? null,
      projectName: cited?.project_name ?? attached?.label ?? null,
    });
    setPanelOpen(true);
  }, []);

  // Hidden either because they said so, or because a project needs the room.
  /* The document owns the right column from the moment writing starts.
   *
   * It is derived from the step rather than being another thing to open: once
   * sections are being written, what the user needs on the right is the
   * proposal — not the project's file list they opened twenty minutes ago.
   * `docDismissed` is the escape hatch, and it resets whenever the flow moves,
   * so closing it means "not right now", not "never again this run". */
  const [docDismissed, setDocDismissed] = useState(false);
  const docPanelOpen =
    !docDismissed &&
    proposal?.chatId === chatId &&
    ["writing", "sectionDone", "complete"].includes(proposal?.step);

  useEffect(() => {
    setDocDismissed(false);
  }, [proposal?.step, proposal?.sectionIdx]);


  /* Sensa asked for a section to be redrafted.
   *
   * The tool only RESOLVES which section; the redraft runs through the same
   * per-section loop the Regenerate button uses, so there is one implementation
   * of "write a section" rather than a second one on the server that would
   * drift from it.
   *
   * Consumed once and cleared. Left in place it would re-fire on every render,
   * and each fire overwrites a section - the most expensive possible loop. */
  useEffect(() => {
    // Same as the export card: let Sensa finish speaking first.
    if (!lastRewrite || streaming) return;
    clearLastRewrite();

    /* Seed the flow rather than requiring one.
     *
     * This used to bail when proposalRef was empty, which is exactly the case
     * that matters most: "generate proposal" in a fresh chat calls
     * continue_proposal, the card arrives, and there is no flow state yet — so
     * the card was dropped and the user saw NOTHING happen. The card carries
     * the project; that is all the state needed to start.
     *
     * The only refusal left is a card for a different project than the flow
     * already running here, which would otherwise write into the wrong bid. */
    const p = proposalRef.current;
    if (p && p.chatId === chatId && p.projectId && p.projectId !== lastRewrite.projectId) return;

    setProposal((prev) => ({
      ...(prev || {}),
      chatId,
      projectId: lastRewrite.projectId,
      projectName: lastRewrite.projectName || prev?.projectName || "",
    }));

    const continuing = lastRewrite.mode === "continue";
    closeFlowCard(
      continuing
        ? `Writing ${lastRewrite.sectionLabel || "the next section"}`
        : `Rewriting ${lastRewrite.sectionLabel || "a section"}`,
      lastRewrite.instruction ? `“${lastRewrite.instruction}”` : "",
      continuing ? "step" : "redo"
    );
    /* Nothing is said here, deliberately.
     *
     * This is the path taken when the request came through CHAT, and on that
     * path the MODEL has already spoken — "Writing the next section now." A
     * line from us underneath it said the same thing a second time in a worse
     * form, with the section number leaking into the sentence ("Drafting 4
     * Module A: ..."). Two voices narrating one action is worse than one.
     *
     * The button-driven transitions still introduce their cards, because there
     * no model turn happens and nothing else would speak. */
    // Project passed explicitly: on a fresh chat there is no previous state for
    // openFlowCard to inherit it from, and a writing card without a project id
    // fetches an outline from `/folders/undefined/`.
    openFlowCard("writing", {
      chatId,
      projectId: lastRewrite.projectId,
      projectName: lastRewrite.projectName || "",
      sectionIdx: lastRewrite.sectionIndex,
      instruction: lastRewrite.instruction,
    });
  }, [lastRewrite, streaming, clearLastRewrite, chatId, closeFlowCard, openFlowCard, say]);

  const openDocPanel = useCallback(() => setDocDismissed(false), []);

  /* Opening a record in the transcript also takes the document to that section.
   *
   * The row answers "what did it write"; the panel answers "how does it read in
   * place, next to what comes before and after". Wanting one almost always
   * means wanting both, and the alternative is scrolling a twelve-section
   * document by hand to find the one you were just looking at.
   *
   * The id is kept in state rather than passed as a scroll command because the
   * panel may not be mounted yet — it is opened on the same click. It scrolls
   * when it has both the id and the document. */
  const [focusSectionId, setFocusSectionId] = useState(null);
  const openDocAtSection = useCallback((sectionId) => {
    if (!sectionId) return;
    setDocDismissed(false);
    setFocusSectionId(String(sectionId));
  }, []);

  /* One bundle of handlers for every flow card, so CopilotTurn takes a single
   * prop rather than eleven that only the proposal rows ever read. */
  const proposalFlow = useMemo(
    () => ({
      onApproachChosen: approachChosen,
      onClarifyDone: clarifyDone,
      onOneGoDone: oneGoDone,
      onOneGoFailed: oneGoFailed,
      onPlanReady: planReady,
      onPlanFailed: planFailed,
      onRebuild: rebuildOutline,
      onOutlineConfirm: outlineConfirmed,
      onSectionWritten: sectionWritten,
      onSectionFailed: sectionFailed,
      onSectionApproved: sectionApproved,
      onTemplateChosen: templateChosen,
      onSectionRewrite: sectionRewrite,
      onAutoContinue: autoContinue,
      docOpen: docPanelOpen,
      onOpenDoc: openDocPanel,
      onOpenSection: openDocAtSection,
    }),
    [
      approachChosen,
      clarifyDone,
      oneGoDone,
      oneGoFailed,
      planReady,
      planFailed,
      rebuildOutline,
      outlineConfirmed,
      sectionWritten,
      sectionFailed,
      sectionApproved,
      templateChosen,
      sectionRewrite,
      autoContinue,
      docPanelOpen,
      openDocPanel,
      openDocAtSection,
    ]
  );

  // The rail pays for the panel either way — the user is reading a document,
  // not switching conversations.
  const railHidden = railCollapsed || panelOpen || docPanelOpen;

  /* One way back, whichever hid it. Two controls that both "show chats" but
   * mean different things is how you end up with a rail that reappears without
   * the project closing, or a project that closes without the rail returning. */
  const showRail = useCallback(() => {
    setRailCollapsed(false);
    closePanel();
  }, [closePanel]);

  const awaitingText = messages[messages.length - 1]?.role !== "assistant";
  const showReasoning = streaming && !!trace && (awaitingText || !!activeTool);

  /* What a screen reader is told, and only when it changes.
   *
   * Four states are worth speaking: waiting, a decision is needed, an answer
   * arrived, something failed. Everything else — each streamed token, each
   * scroll — is noise that would bury them. */
  const last = messages[messages.length - 1];
  const liveStatus = streaming
    ? "Sensa is working"
    : last?.role === "approval" && !last?.resolved
    ? "Sensa needs your confirmation"
    : last?.role === "error"
    ? `Error: ${last.text}`
    : last?.role === "assistant" && last?.text
    ? "Sensa replied"
    : "";

  /* Follow the tail — but only when the reader is already there.
   *
   * This effect fires on every streamed token. Scrolling unconditionally meant
   * that scrolling up to re-read something yanked you back to the bottom a few
   * milliseconds later, which makes a long answer impossible to read while it
   * is still being written.
   *
   * `auto`, not `smooth`: a smooth scroll per token queues dozens of competing
   * animations and the view visibly stutters.
   */
  const BOTTOM_SLACK = 80;   // px of "close enough to the bottom to count"

  /* Position and intent are different things, and conflating them broke both.
   *
   * `scroll` fires for OUR scrolling as much as the user's. So the moment the
   * view anchored below the bottom, the scroll handler concluded the user had
   * scrolled away and switched following off — the anchor then held only
   * because the effect had stopped running, and the "still writing" pill was
   * driven by a flag that no longer meant what it said.
   *
   * Now the scroll handler only reports WHERE we are, and following is turned
   * off by the wheel and touch gestures that mean a person did it. Scrolling
   * back to the bottom opts back in, which is the one unambiguous "I want to
   * follow again" signal there is.
   */
  const measurePinned = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance <= BOTTOM_SLACK) followRef.current = true;
    setShowJump(distance > BOTTOM_SLACK * 2);
  }, []);

  // Only a gesture stops the follow. A wheel or a drag upward is a person
  // deciding to read something; a scrollTop we set ourselves is not.
  const handleUserScrollIntent = useCallback((e) => {
    if (e?.type === "wheel" && e.deltaY >= 0) return;
    followRef.current = false;
  }, []);

  const scrollToBottom = useCallback((smooth = false) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    followRef.current = true;
    setShowJump(false);
  }, []);

  /* Follow the answer's TOP, not its bottom.
   *
   * Chasing the bottom means the text slides upward under the reader for as
   * long as the model keeps writing — you cannot finish a sentence before it
   * moves. The fix is not to stop scrolling altogether, which would leave a
   * long answer growing entirely off-screen: it is to scroll only until the
   * START of the reply reaches the top of the view, and then hold there.
   *
   * min() does both jobs with no mode switch. While the answer is shorter than
   * the viewport, `maxScroll` is the smaller number and the view follows the
   * bottom exactly as before, so short replies appear whole. The moment the
   * answer outgrows the screen, the anchor becomes the smaller number and the
   * view stops dead with the first line pinned to the top — the rest arrives
   * below the fold, which is what the "still writing" pill is for.
   */
  const ANCHOR_PAD = 12;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    const live = el.querySelector("[data-anchor-turn]");

    if (followRef.current) {
      if (live) {
        const desired = Math.min(Math.max(live.offsetTop - ANCHOR_PAD, 0), maxScroll);
        // Never travel backwards: an answer that briefly reflows shorter must
        // not drag the reader up a line.
        el.scrollTop = Math.max(el.scrollTop, desired);
      } else {
        /* No live turn. Following now means ONLY "keep up with new content
         * while I am already at the bottom".
         *
         * It used to fall through to maxScroll here, and the anchor element
         * disappears the instant streaming stops — so finishing an answer threw
         * the reader to the bottom of it, undoing the whole point of holding
         * position while they read. */
        if (el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_SLACK) {
          el.scrollTop = maxScroll;
        } else {
          /* Parked in the middle of a finished answer. They are reading, not
           * following — say so, rather than leaving a flag set that would let
           * some later append jump the view without warning. Pressing Latest
           * turns it back on. */
          followRef.current = false;
        }
      }
    }

    /* The pill is recomputed here, not only in the scroll handler.
     * Content growing below the fold fires no scroll event, so while the view
     * is anchored and the answer is still arriving, nothing was telling the
     * button it had a reason to exist. */
    setShowJump(el.scrollHeight - el.scrollTop - el.clientHeight > BOTTOM_SLACK * 2);
  }, [messages, activeTool, showReasoning, streaming]);

  // Pre-fill and focus rather than sending outright — a suggestion is a
  // starting point, and people almost always want to add a detail before
  // committing. Auto-sending robs them of that without saving a click.
  const handleSuggestion = (text) => {
    setDraft(text);
    composerRef.current?.focus();
  };

  const project = useMemo(() => context.find((c) => c.kind === "project") || null, [context]);
  contextProjectRef.current = project;

  useEffect(() => {
    if (ctxState.owner !== chatId) return;
    writeContext(chatId, ctxState.items);
  }, [chatId, ctxState]);

  /* The server-side pin, applied once the chat it belongs to is on screen.
   *
   * Local context still wins when present — it can be newer than the last
   * persisted turn (the user re-pinned and hasn't sent a message yet). The
   * server tag fills the gap localStorage loss creates: logout, new browser,
   * another device. Held in a ref until chatId matches, because restore rows
   * can arrive while a different conversation is still mounted.
   */
  const pendingProjectRef = useRef(null);
  const [restoreTick, setRestoreTick] = useState(0);
  const queueRestoredProject = (cid, rows) => {
    const proj = projectFromRows(rows);
    if (!proj) return;
    pendingProjectRef.current = { cid, proj };
    setRestoreTick((t) => t + 1);
  };
  useEffect(() => {
    const pending = pendingProjectRef.current;
    if (!pending || pending.cid !== chatId) return;
    pendingProjectRef.current = null;
    setContextItems((prev) =>
      prev.some((c) => c.kind === "project")
        ? prev
        : [...prev, { kind: "project", id: pending.proj.id, label: pending.proj.label }]
    );
  }, [chatId, restoreTick, setContextItems]);

  // Each conversation seeds from its own stored context. A brand-new id has
  // none, so a new chat is clean by construction — no special case needed.
  useEffect(() => {
    setCtxState((prev) =>
      prev.owner === chatId ? prev : { owner: chatId, items: readContext(chatId) }
    );
  }, [chatId]);

  /* "Chat with project", arriving from a project page.
   *
   * The project's own conversation: the first click starts one — the project
   * pinned as the chip, its documents open on the right — and every later click
   * goes back to that same conversation, with its history, rather than starting
   * another. The project is not bolted onto whatever was being discussed before.
   *
   * Starting or switching chats is two steps because the context is stored per
   * chat: the id changes first, and only once the new id has landed can the
   * project be attached to it (attaching first would write it into the OLD
   * chat). The navigation state is cleared at once, so a refresh does not repeat
   * any of it. */
  const attachRef = useRef(null);
  const handledNavRef = useRef(null);
  const applyAttach = useCallback(
    (target) => {
      setContextItems((prev) => [
        ...prev.filter((c) => c.kind !== "project"),
        { kind: "project", id: target.projectId, label: target.projectName || "Project" },
      ]);
      openProjectDocuments(target);
    },
    [setContextItems, openProjectDocuments]
  );
  useEffect(() => {
    const target = location.state?.chatWithProject;
    if (!target?.projectId || handledNavRef.current === location.key) return;
    handledNavRef.current = location.key;
    attachRef.current = { ...target, fromChatId: chatId };
    navigate(location.pathname, { replace: true, state: null });

    const startNew = () => reset();
    const existing = readProjectChat(target.projectId);
    if (!existing) {
      startNew();
      return;
    }
    if (existing === chatId) {
      // Already in it: nothing to switch, just bring the project's panel up.
      attachRef.current = null;
      applyAttach(target);
      return;
    }
    api
      .get(`/api/v1/copilot/get-chat-sessions/${existing}/?chat_module=workspace_copilot`)
      .then((res) => {
        const rows = res?.data?.chat_history || [];
        if (!rows.length) return startNew(); // nothing was ever said in it
        queueRestoredProject(existing, rows);
        loadSession(existing, rows);
        setDraft("");
      })
      .catch(startNew); // deleted, or not this user's
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  useEffect(() => {
    const target = attachRef.current;
    if (!target || target.fromChatId === chatId) return;
    attachRef.current = null;
    rememberProjectChat(target.projectId, chatId);
    applyAttach(target);
  }, [chatId, applyAttach]);

  // The menu is open purely as a function of what is typed, so it can never be
  // left showing over stale text. Escape sets a dismiss flag the next edit clears.
  /* The project picker, opened by the "+" beside the input rather than by a
   * typed trigger. Its query is its own state: nothing about choosing a project
   * belongs in the message being written. */
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const typedParse = useMemo(() => (slashDismissed ? null : parseSlash(draft)), [draft, slashDismissed]);
  const parsed = useMemo(
    () =>
      pickerOpen
        ? { mode: "argument", command: PROJECT_COMMAND, query: pickerQuery, picker: true }
        : typedParse,
    [pickerOpen, pickerQuery, typedParse]
  );

  const handleDraftChange = (value) => {
    setDraft(value);
    if (slashDismissed) setSlashDismissed(false);
  };

  /* /qualify — the bid / no-bid card.
   *
   * GET returns the saved analysis instantly; a 404 means it has never run, so
   * we POST to run it fresh (slow — it scores every requirement) and say so
   * honestly in the card rather than showing a generic spinner.
   */
  // The card only renders a subset of the readiness payload; persist exactly
  // that subset. The full object carries every requirement with source excerpts
  // — hundreds of KB the restored card would never show.
  const compactReadiness = (d) => ({
    "readiness score": d["readiness score"],
    // Kept through compaction: a restored card that cannot say what it excluded
    // is a restored card that overstates itself.
    extracted_requirements: d.extracted_requirements,
    // Survives compaction too — a restored card that cannot say what it left
    // out is a restored card that overstates itself.
    excluded_count: d.excluded_count,
    // Derived here and stored, because the per-requirement scores it comes from
    // are exactly what the compact payload drops. Without this a restored card
    // silently loses its most useful panel.
    evidence_distribution: (() => {
      const c = { strong: 0, partial: 0, weak: 0, none: 0 };
      let seen = 0;
      (d["capability and fit check"] || []).forEach((a) =>
        (a.requirements || []).forEach((r) => {
          const q = Number(r.confidence_score) || 0;
          if (q >= 4) c.strong += 1;
          else if (q === 3) c.partial += 1;
          else if (q >= 1) c.weak += 1;
          else c.none += 1;
          seen += 1;
        })
      );
      return seen ? c : null;
    })(),
    verdict: d.verdict,
    verdict_recommendation: d.verdict_recommendation,
    // Kept through compaction: without it a restored card cannot say whether
    // it was judged on this RFP's own criteria or the organisation's preset,
    // and those are not the same claim.
    criteria_source: d.criteria_source,
    "capability and fit check": (d["capability and fit check"] || []).map((a) => ({
      name: a.name,
      weight: a.weight,
      score_sum: a.score_sum,
      requirements_total: a.requirements_total,
    })),
    critical_gaps: d.critical_gaps || [],
    // Kept without the `sources` chunks — those are whole passages of RFP text
    // and the card never renders them.
    evaluation_qa: (d.evaluation_qa || []).map((q) => ({
      question: q.question,
      answer: q.answer,
      found: q.found,
    })),
    total_requirements: d.total_requirements,
    analyzed_at: d.analyzed_at,
  });

  // Card turns never pass through the agent, so nothing persists them server-
  // side — a chat holding only cards would vanish from history entirely. Log
  // each completed card as a normal-looking turn; failure is swallowed because
  // history is a bonus, and the card on screen is the actual deliverable.
  const logQualifyTurn = useCallback(
    async (cid, target, data, msgId) => {
      try {
        const res = await api.post("/api/v1/rfp/copilot/log-turn/", {
          chat_id: cid,
          project_id: target.id,
          user_message: `/qualify — ${target.label}`,
          title: `Qualify: ${target.label}`,
          bot_message:
            QUALIFY_MARKER +
            JSON.stringify({
              projectLabel: target.label,
              projectId: target.id,
              data: compactReadiness(data),
            }),
        });
        setHistoryKey((k) => k + 1);
        // The row this card was saved as. Attaching the ids is what makes the
        // thumbs under it real — without them they render disabled and the
        // highest-stakes answer in the product is the one nobody can rate.
        if (msgId && res?.data?.message_id) {
          patchLocal(msgId, { messageId: res.data.message_id, runId: res.data.run_id });
        }
      } catch {
        /* the card already rendered; the rail will catch up on the next turn */
      }
    },
    [patchLocal]
  );

  /* /qualify, end to end.
   *
   * A project can be at any point on the path: documents not read yet,
   * requirements never extracted, extracted but never scored, or fully
   * analysed. One status call says which, and this walks the rest of the way
   * without the user having to know any of those stages exist.
   *
   * Extraction results are saved on the documents by the backend, so this cost
   * is paid once per project — every later /qualify is instant.
   */
  const POLL_MS = 2500;

  /* Watches a scoring run that is already in flight.
   *
   * The readiness POST is one long call that returns nothing until it is
   * completely done, so the phases inside it — triage, then the knowledge-base
   * search — are published by the run and read back here. A failed poll is
   * cosmetic and never touches the run, so it is swallowed: losing the step
   * display must not lose the analysis.
   *
   * Returns its own stop function; the caller must run it in a `finally`, or a
   * failed run leaves this polling forever.
   */
  const watchScoring = useCallback(
    (folderId, msgId, requirements) => {
      let live = true;
      (async () => {
        while (live) {
          await new Promise((r) => setTimeout(r, POLL_MS));
          if (!live) return;
          try {
            const { progress } = (
              await api.get(`/api/v1/rfp/folders/${folderId}/qualify-status/`)
            ).data;
            if (!live || !progress) continue;
            patchLocal(msgId, {
              stage: progress.phase === "scoring" ? "scoring" : "triaging",
              requirements,
              scoringTotal: progress.total,
              excludedCount: progress.excluded,
            });
          } catch {
            /* the run is what matters — keep watching */
          }
        }
      })();
      return () => {
        live = false;
      };
    },
    [patchLocal]
  );

  /* The "Summary" section that follows /qualify's score, WITHIN the same
   * card — fitment against the company's own knowledge base (sector,
   * capability, geography, precedent), a different question from the
   * requirement-by-requirement score above it: that one asks "does the
   * evidence in our KB match THIS RFP's text"; this one asks "does this KIND
   * of opportunity match what the company does at all". Same two-call shape
   * and the same backend endpoints (/recall/ + /recall/enrich/) the
   * tender-search page's "Check against your Memory" button already uses,
   * so the two screens never disagree.
   *
   * Patched onto the QUALIFY message itself (fitSummary*), never posted as
   * its own message — a second message renders as a second, visually
   * separate card no matter how it is labelled, and this belongs under the
   * one card /qualify already produced.
   */
  const runFitSummary = useCallback(
    async (target, msgId) => {
      if (!target) return;
      patchLocal(msgId, { fitSummaryState: "loading" });

      let base;
      try {
        // The fast call carries the header only — see RFPRecallView.
        base = (await api.get(`/api/v1/rfp/folders/${target.id}/recall/`)).data;
      } catch {
        patchLocal(msgId, {
          fitSummaryState: "error",
          fitSummaryError: "Couldn't check this against your knowledge base.",
        });
        return;
      }

      try {
        const rich = (
          await api.get(`/api/v1/rfp/folders/${target.id}/recall/enrich/`, {
            timeout: 75000,
          })
        ).data;
        patchLocal(msgId, { fitSummaryState: "ready", fitSummary: { ...base, ...rich } });
      } catch {
        patchLocal(msgId, { fitSummaryState: "ready", fitSummary: base });
      }
    },
    [patchLocal]
  );

  const runQualify = useCallback(
    async (proj) => {
      const target = proj || project;
      if (!target) return;
      const cid = chatId;
      const msgId = postLocal({
        role: "qualify",
        state: "loading",
        stage: "checking",
        projectLabel: target.label,
        projectId: target.id,
      });

      const statusUrl = `/api/v1/rfp/folders/${target.id}/qualify-status/`;
      const fail = (msg) => patchLocal(msgId, { state: "error", error: msg });

      // Extraction is Celery work with no completion callback, so its progress
      // has to be observed. Bounded rather than open-ended: a stuck worker must
      // surface as a clear message, never as a spinner that runs all afternoon.
      const MAX_WAIT_MS = 8 * 60 * 1000;
      const startedAt = Date.now();

      try {
        let status = (await api.get(statusUrl)).data;
        let requestedExtract = false;

        for (;;) {
          patchLocal(msgId, {
            // needs_scoring means the whole scoring call is still ahead, and
            // that call starts with triage — so it maps there, not to
            // "scoring". Mapping it to "scoring" made the very next patch move
            // the card BACKWARDS a step, which reads as a glitch.
            stage: status.stage === "needs_scoring" ? "triaging" : status.stage,
            documents: status.documents,
            requirements: status.requirements,
          });

          if (status.stage === "no_documents") {
            return fail("This project has no documents yet. Upload the RFP first, then run /qualify.");
          }
          if (status.stage === "not_ingested") {
            return fail("The documents are still being processed for search. Try /qualify again in a few minutes.");
          }
          if (status.stage === "ready" && status.analysis) {
            patchLocal(msgId, {
              state: "ready",
              data: { ...status.analysis, extracted_requirements: status.requirements },
            });
            logQualifyTurn(cid, target, status.analysis, msgId);
            runFitSummary(target, msgId);
            return;
          }

          if (status.stage === "needs_extract") {
            if (!requestedExtract) {
              // Requirements only — dates are a separate concern and asking for
              // both would double the work for no benefit here.
              requestedExtract = true;
              patchLocal(msgId, { stage: "extracting" });
              await api.post(
                `/api/v1/rfp/folders/${target.id}/insights/extract/?insights=requirements`
              );
            } else {
              // Asked, and the server still reports nothing extracted and
              // nothing running — the tasks are not going to produce anything.
              return fail(
                "Couldn't pull requirements out of these documents. They may be scanned images or an unsupported layout."
              );
            }
          } else if (status.stage === "needs_scoring") {
            // Triage runs first inside this call, so that is where the card
            // starts. The watcher below moves it on when the server says so —
            // never on a timer, because a step that advances on a guess is one
            // nobody trusts the second time they see it.
            patchLocal(msgId, { stage: "triaging" });
            const stop = watchScoring(target.id, msgId, status.requirements);
            try {
              const run = await api.post(`/api/v1/rfp/folders/${target.id}/readiness/`);
              patchLocal(msgId, {
                state: "ready",
                data: { ...run.data, extracted_requirements: status.requirements },
              });
              logQualifyTurn(cid, target, run.data, msgId);
              runFitSummary(target, msgId);
            } finally {
              stop();
            }
            return;
          }

          if (Date.now() - startedAt > MAX_WAIT_MS) {
            return fail("This is taking longer than expected. The extraction is still running — try /qualify again shortly.");
          }
          await new Promise((r) => setTimeout(r, POLL_MS));
          status = (await api.get(statusUrl)).data;
        }
      } catch (err) {
        fail(
          err?.response?.data?.error ||
            err?.response?.data?.msg ||
            "Couldn't run the bid check. Try again in a moment."
        );
      }
    },
    [project, chatId, postLocal, patchLocal, logQualifyTurn, watchScoring, runFitSummary]
  );

  const handleRerunQualify = useCallback(
    async (msg) => {
      // A rerun re-does the whole scoring call, triage included, so it shows
      // the same steps rather than a bare wait.
      patchLocal(msg.id, {
        state: "loading",
        stage: "triaging",
        data: undefined,
        error: undefined,
        scoringTotal: undefined,
        excludedCount: undefined,
      });
      const stop = watchScoring(msg.projectId, msg.id, msg.requirements);
      try {
        const run = await api.post(`/api/v1/rfp/folders/${msg.projectId}/readiness/`);
        patchLocal(msg.id, { state: "ready", data: run.data });
        logQualifyTurn(chatId, { id: msg.projectId, label: msg.projectLabel }, run.data);
      } catch (err) {
        patchLocal(msg.id, {
          state: "error",
          error: err?.response?.data?.error || "The analysis couldn't run. Try again in a moment.",
        });
      } finally {
        stop();
      }
    },
    [patchLocal, chatId, logQualifyTurn, watchScoring]
  );

  // /summary — the same "summarize this project" question the assistant
  // already answers in plain chat, wired as a one-key shortcut. No bespoke
  // card, no new endpoint: it just sends the question for you, exactly as
  // typing it and pressing Enter would (see handleSend just above).
  const runSummary = useCallback(
    (proj) => {
      const target = proj || project;
      if (!target) return;
      send("Summarize this project.", { projectId: target.id });
    },
    [project, send]
  );

  // /qualify and /summary with nothing pinned open the project picker and
  // remember why — the natural order is "pick, then act", so we chain rather
  // than refuse.
  const pendingQualifyRef = useRef(false);
  const pendingSummaryRef = useRef(false);

  const handlePickCommand = (cmd) => {
    if (cmd.takesArgument === false) {
      setDraft("");
      if (cmd.key === "qualify") {
        if (!project) {
          pendingQualifyRef.current = true;
          setDraft("@");
        } else {
          runQualify();
        }
      }
      if (cmd.key === "summary") {
        if (!project) {
          pendingSummaryRef.current = true;
          setDraft("@");
        } else {
          runSummary();
        }
      }
      composerRef.current?.focus();
      return;
    }
    // Complete the word and add the space that commits it, which drops straight
    // into that command's picker.
    setDraft(`${cmd.trigger} `);
    composerRef.current?.focus();
  };

  const handlePickProject = (p) => {
    setContextItems((prev) => [
      ...prev.filter((c) => c.kind !== "project"),
      { kind: "project", id: p.id, label: p.name, sublabel: p.status },
    ]);
    // Attaching a project must never eat the question being written. The picker
    // is opened from a button and has its own search box, so the draft is not
    // involved either way — it is left exactly as the user typed it.
    if (parsed?.picker) {
      setPickerOpen(false);
      setPickerQuery("");
    } else {
      setDraft("");
    }
    composerRef.current?.focus();
    // A /qualify or /summary that was waiting on a project runs now, against
    // the project just picked — passed directly, because the context state
    // lands async.
    if (pendingQualifyRef.current) {
      pendingQualifyRef.current = false;
      runQualify({ id: p.id, label: p.name });
    }
    if (pendingSummaryRef.current) {
      pendingSummaryRef.current = false;
      runSummary({ id: p.id, label: p.name });
    }
  };

  const handleRemoveContext = (item) => {
    setContextItems((prev) => prev.filter((c) => !(c.kind === item.kind && c.id === item.id)));
    // The viewer opened for this project's documents has nothing left to be
    // about; one showing an answer's citations stays.
    if (
      item.kind === "project" &&
      panelSource &&
      !panelSource.sources?.length &&
      String(panelSource.projectId) === String(item.id)
    ) {
      closePanel();
    }
  };

  const registerKeyHandler = useCallback((fn) => {
    menuKeyRef.current = fn;
  }, []);

  const handleComposerKey = (e) => (menuKeyRef.current ? menuKeyRef.current(e) : false);

  const handleSend = () => {
    if (!draft.trim() || streaming) return;

    /* A new question ends the flow, exactly as the stop button does.
     *
     * Leaving a live card up while answering something else gives the user two
     * live threads and one composer: they ask about pricing, get an answer, and
     * an Approve button from ten minutes ago is still sitting there waiting to
     * write section 8. Whichever they press next, one of the two was not what
     * they meant.
     *
     * So moving on is treated as a decision. The card collapses into the same
     * "Stopped" record the button produces, and asking again picks it back up —
     * nothing written is lost, only the pending prompt. */
    if (flowRowRef.current) {
      closeFlowCard("Stopped", "ask me to write it again whenever you're ready", "redo");
      setProposal((prev) => (prev ? { ...prev, step: "stopped" } : prev));
    }

    // projectId is sent explicitly rather than left to the agent to infer. The
    // chip is a promise about which project this question is about.
    // Always send project_id — null when nothing is pinned, never omitted. The
    // server only reacts to the key being PRESENT, so omitting it on an empty
    // chip left the previously pinned project in place: removing the chip
    // looked like it worked and changed nothing.
    send(draft, { projectId: project ? project.id : null });
    markOnboarding("sensa");
    setDraft("");
    // Sending is an explicit "show me what happens next", so it re-pins even if
    // the reader had scrolled away while composing.
    followRef.current = true;
    setShowJump(false);
  };

  const handleNewChat = () => {
    reset();
    setDraft("");
    // No context write here: chatId hasn't changed yet, so clearing now would
    // hit the OLD chat's key and erase the context it should keep. The seed
    // effect swaps to the (empty) new chat as soon as the new id lands.
    composerRef.current?.focus();
  };

  const handleSelectSession = (id, history) => {
    queueRestoredProject(id, history);
    loadSession(id, history);
    setDraft("");
  };

  // The agent can switch project itself via switch_project. That has to land in
  // the same chip the user set, or the bar would claim one project while the
  // answers came from another.
  useEffect(() => {
    if (!focus?.project_id) return;
    setContextItems((prev) => {
      const current = prev.find((c) => c.kind === "project");
      if (current && current.id === focus.project_id) return prev;
      return [
        ...prev.filter((c) => c.kind !== "project"),
        { kind: "project", id: focus.project_id, label: focus.project_name || "Project" },
      ];
    });
  }, [focus]);

  // The backend writes the turn only once the reply completes, so the listing is
  // stale until then. Refresh on that edge rather than polling.
  useEffect(() => {
    if (streaming) return;
    setHistoryKey((k) => k + 1);
  }, [streaming]);

  useEffect(() => {
    const el = composerBoxRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => {
      setComposerH(Math.round(entry.contentRect.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  }, []);

  return (
    <div className="flex h-full overflow-hidden bg-white font-outfit">
      {/* Permanent, not a drawer. Past chats and "new chat" are the two things
          people reach for most here, so neither should be a glyph you have to
          discover. The app sidebar collapses on this route to pay for the
          width — see Layout.jsx. */}
      {/* The rail yields to the project panel outright. Three columns is the
          most this route can carry, and when someone opens a project the
          conversation plus that project IS the task — a list of other chats
          is the least relevant thing on screen. Closing the panel brings it
          straight back. */}
      <div
        className={`h-full flex-none overflow-hidden transition-[width] duration-300 ease-sidebar motion-reduce:transition-none ${
          /* 276px, matching CopilotHistory's own fixed width exactly.
           *
           * It was 248 (268 at xl) against a 276px child, so the rail clipped
           * roughly 28px of itself permanently — every row truncated early and
           * the whole column looked squashed against the sidebar. The child is
           * deliberately flex-none so the width animation REVEALS it rather
           * than reflowing every row; that only works if the open width is the
           * child's width. */
          railHidden ? "w-0" : "w-[276px]"
        }`}
      >
        <CopilotHistory
          onCollapse={() => setRailCollapsed(true)}
          activeChatId={chatId}
          onSelect={handleSelectSession}
          onNewChat={handleNewChat}
          refreshKey={historyKey}
        />
      </div>

      <div className="relative flex min-w-0 flex-1 flex-col">
        {/* Behind everything, and only while the page is empty. Once a
            conversation exists the content is the interest, and moving
            colour behind text you are trying to read is just noise. */}
        {isEmpty && <CopilotAmbient />}

        <div className="relative z-10 shrink-0">
          <CopilotHeader showChatsButton={railHidden} onShowChats={showRail} />
        </div>

        <div
          ref={scrollRef}
          onScroll={measurePinned}
          onWheel={handleUserScrollIntent}
          onTouchMove={handleUserScrollIntent}
          /* The composer sits OVER this, not beside it.
           *
           * As siblings in a column the scroll area simply ended where the
           * composer began, so text was CLIPPED mid-line against an opaque
           * strip — and the composer's fade-out gradient had nothing behind it
           * to fade, which is why softening the gradient changed nothing. With
           * the composer overlaid and this padded to clear it, the conversation
           * genuinely runs underneath and dissolves. */
          className="relative z-10 flex-1 min-h-0 overflow-y-auto sensa-scroll"
          style={{ scrollPaddingBottom: composerH }}
        >
          {isEmpty ? (
            // Centred in the space it has, rather than pinned to the top with a
          // dead third of the page between the cards and the composer.
          <div
              className="flex min-h-full items-center justify-center px-6"
              style={{ paddingBottom: composerH }}
            >
              {project ? (
                <CopilotProjectOverview
                  projectId={project.id}
                  projectName={project.label}
                  onAsk={handleSuggestion}
                  onOpenDocuments={() => openProjectDocuments({ projectId: project.id, projectName: project.label })}
                  onManageFiles={() => openProjectPanel({ projectId: project.id, projectName: project.label })}
                />
              ) : (
                <CopilotEmptyState
                  greeting={greeting}
                  displayName={displayName}
                  onPick={handleSuggestion}
                  picks={picks}
                  onOpenPick={openTenderPanel}
                  onSeeAllPicks={() => navigate("/tender-search/discover")}
                />
              )}
            </div>
          ) : (
            // One column shared by the header, turns and composer, so the
            // conversation sits on a single spine instead of drifting.
            // gap-7 rather than gap-4: turns need room to read as separate
            // thoughts now that the assistant has no box around it.
            <div className="mx-auto flex max-w-[860px] flex-col gap-7 px-6 py-8" role="log">
              {/* The project's summary stays with the conversation: the opening
                  overview is the empty screen and goes with the first message. */}
              {project && (
                <CopilotProjectSummary
                  projectId={project.id}
                  projectName={project.label}
                  onOpenDocuments={() => openProjectDocuments({ projectId: project.id, projectName: project.label })}
                />
              )}
              {messages.map((m, i) => (
                <div
                  key={m.id}
                  // The anchor point. Only the turn currently being written
                  // carries it, so the effect above always finds the reply the
                  // reader is waiting on and never an older one.
                  {...(streaming && i === lastUserIndex ? { "data-anchor-turn": "" } : {})}
                >
                <CopilotTurn
                  key={m.id}
                  message={m}
                  prev={messages[i - 1]}
                  next={messages[i + 1]}
                  feedbackFor={feedbackAt[i]}
                  opensTurn={speakerAt[i]}
                  streaming={streaming && i === messages.length - 1}
                  onCite={openSourcePanel}
                  onOpenTender={openTenderPanel}
                  onTenderProjectCreated={handleTenderProjectCreated}
                  activeTenderId={panelOpen ? panelTender?.tenderId : null}
                  /* The streaming turn has no trace parked on it yet, so its
                     citations resolve against the live one. Tool results land
                     before the text that cites them, so a mark is clickable
                     from the moment it appears. */
                  liveTrace={streaming && i === messages.length - 1 ? trace : null}
                  onOpenAspect={(aspectName) => {
                    const pid = m.projectId || project?.id;
                    if (!pid) return;
                    openAspectPanel({
                      projectId: pid,
                      projectLabel: m.projectLabel || project?.label,
                      aspect: aspectName,
                    });
                  }}
                  onOpenBand={(band) => {
                    const pid = m.projectId || project?.id;
                    if (!pid) return;
                    // No aspect: an evidence band is a question about the whole
                    // project, not about one area of it.
                    openAspectPanel({
                      projectId: pid,
                      projectLabel: m.projectLabel || project?.label,
                      aspect: null,
                      band,
                    });
                  }}
                  onQualifyRerun={handleRerunQualify}

                  onApprovalRespond={respondToApproval}
                  onOpenProject={openProjectPanel}
                  onProposalStart={startProposal}
                  proposalCarry={proposalCarryRef.current}
                  onProposalCarry={rememberProposalSetup}
                  flow={proposalFlow}
                />
                </div>
              ))}

              {/* The wait — see showReasoning above. */}
              {showReasoning && <CopilotReasoningLive trace={trace} />}
              {/* Room for the floating composer, so the last line can always be
                  scrolled clear of it. */}
              <div aria-hidden="true" style={{ height: composerH }} />

              {/* Spoken status, for people who cannot see the screen.
                  Deliberately NOT aria-live on the transcript itself: a reply
                  streams token by token, and a live region over it would
                  re-announce the growing paragraph on every frame — unusable.
                  This announces the few transitions that actually matter. */}
              <p className="sr-only" aria-live="polite" aria-atomic="true">
                {liveStatus}
              </p>
              <div ref={tailRef} />
            </div>
          )}
        </div>

        {/* A socket that never opened is a different failure from one that
            dropped, and saying "lost connection" for both sends people looking at
            their wifi when the endpoint isn't deployed. */}
        {status === "error" && (
          <div className="shrink-0 px-6 pb-1">
            <p className="mx-auto max-w-[760px] text-center text-[13px] text-[#B91C1C]">
              {lastClose && !lastClose.everOpened
                ? "Couldn't reach the copilot service. If this persists, the workspace-copilot endpoint may not be available on this environment yet."
                : "Lost connection to the copilot. Your next message will try to reconnect."}
              {lastClose?.code ? (
                <span className="ml-1 text-[#77707E]">(code {lastClose.code})</span>
              ) : null}
            </p>
          </div>
        )}

        <div
          ref={composerBoxRef}
          className="pointer-events-none absolute inset-x-0 bottom-0 z-20"
        >
          {/* Only while there is something below to go back to. Sits above the
              composer rather than inside the scroll area so it cannot drift
              off-screen with the content it refers to. */}
          {showJump && (
            <div className="pointer-events-none absolute -top-5 left-0 right-0 flex justify-center">
              <button
                type="button"
                onClick={() => scrollToBottom(true)}
                aria-label="Jump to latest"
                className="pointer-events-auto flex h-9 items-center gap-1.5 rounded-full border border-[#18101C]/[0.08] bg-white pl-3 pr-3.5 text-[13.5px] font-medium text-[#3F3A45] shadow-[0_2px_6px_rgba(24,16,28,0.08),0_10px_24px_-12px_rgba(24,16,28,0.4)] transition-[transform,box-shadow] duration-200 ease-sidebar hover:-translate-y-[1px] hover:shadow-[0_3px_8px_rgba(24,16,28,0.1),0_14px_30px_-14px_rgba(24,16,28,0.5)] active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] animate-copilot-rise motion-reduce:transition-none motion-reduce:animate-none"
              >
                {/* One label, both states. It was showing bouncing dots and
                    "Still writing" mid-stream, which read as a status rather
                    than a control — and a control is what it is, at any moment.
                    The arrow says where it takes you; that is true whether the
                    answer has finished or not. */}
                <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <polyline points="19 12 12 19 5 12" />
                </svg>
                Latest
              </button>
            </div>
          )}
          <CopilotComposer
          ref={composerRef}
          projectPickerOpen={pickerOpen}
          onPickProject={() => {
            setPickerQuery("");
            setPickerOpen((v) => !v);
          }}
          value={draft}
          onChange={handleDraftChange}
          onSend={handleSend}
          onStop={stopEverything}
          /* The button follows the WORK; the textarea follows the MODEL.
           *
           * `streaming={busy}` keeps the square up for the whole flow, so there
           * is always an obvious way to stop. But `disabled` is streaming only:
           * locking the textarea through a twelve-section run meant the one
           * moment someone has an opinion about the draft was the one moment
           * they could not type it. Sending now ends the flow the same way the
           * square does — see handleSend. */
          streaming={busy}
          stopping={stopping}
          disabled={streaming}
          onKeyDownCapture={handleComposerKey}
          above={
            <>
              <CopilotSlashMenu
                parsed={parsed}
                onPickCommand={handlePickCommand}
                onPickProject={handlePickProject}
                onClose={() => {
                  if (pickerOpen) {
                    setPickerOpen(false);
                    setPickerQuery("");
                  } else {
                    setSlashDismissed(true);
                  }
                  // The picker's search box held focus; without this, Escape
                  // strands the keyboard on a removed element.
                  composerRef.current?.focus();
                }}
                onQueryChange={(v) => {
                  if (pickerOpen) {
                    setPickerQuery(v);
                    return;
                  }
                  handleDraftChange(v);
                  // Backing out of the picker (draft no longer ends in the
                  // command space) unmounts the search box — return the caret
                  // to the composer so typing keeps working mid-flow.
                  if (!/\s$/.test(v) && !v.includes(" ")) composerRef.current?.focus();
                }}
                registerKeyHandler={registerKeyHandler}
              />
              <CopilotContextBar
                items={context}
                onRemove={handleRemoveContext}
                onOpen={(item) => openProjectDocuments({ projectId: item.id, projectName: item.label })}
              />
            </>
          }
        />
        </div>
      </div>

      {/* Third column, and the reason the history rail steps aside above. */}
      {/* One column, whichever is showing. The requirements view wins while it
          is open and hands back to the project it came from on Back. */}
      {/* First, because a proposal being written outranks whatever else was
          left open in this slot. */}
      {/* A clicked citation wins the column outright — see openSourcePanel. */}
      {panelSource ? (
        <CopilotSourcePanel
          sources={panelSource.sources}
          activeN={panelSource.activeN}
          projectId={panelSource.projectId}
          projectName={panelSource.projectName}
          visible={panelOpen}
          onClose={closePanel}
          onManage={openProjectPanel}
        />
      ) : panelTender ? (
        <CopilotTenderPanel
          key={`tender-${panelTender.tenderId}`}
          tenderId={panelTender.tenderId}
          initial={panelTender.initial}
          visible={panelOpen}
          onClose={closePanel}
          onProjectCreated={handleTenderProjectCreated}
        />
      ) : docPanelOpen ? (
        <ProposalDocumentPanel
          key={`doc-${proposal.projectId}`}
          projectId={proposal.projectId}
          projectName={proposal.projectName}
          activeIdx={proposal.sectionIdx || 0}
          step={proposal.step}
          focusSectionId={focusSectionId}
          onFocusHandled={() => setFocusSectionId(null)}
          visible
          onClose={() => setDocDismissed(true)}
        />
      ) : panelAspect ? (
        <CopilotRequirementsPanel
          key={`${panelAspect.projectId}:${panelAspect.aspect || "*"}:${panelAspect.band || ""}`}
          projectId={panelAspect.projectId}
          projectLabel={panelAspect.projectLabel}
          aspect={panelAspect.aspect}
          initialBand={panelAspect.band}
          visible={panelOpen}
          onClose={closePanel}
          onBack={
            panelProject
              ? () => setPanelAspect(null)
              : undefined
          }
        />
      ) : panelProject ? (
        <CopilotProjectPanel
          key={panelProject.projectId}
          projectId={panelProject.projectId}
          projectName={panelProject.projectName}
          visible={panelOpen}
          onClose={closePanel}
        />
      ) : null}
    </div>
  );
};

export default RfpCopilotPage;
